import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import { 
  Palette, 
  RefreshCw, 
  Smartphone, 
  Search, 
  Server, 
  Plug, 
  ChevronRight,
  CheckCircle
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const services = [
  {
    icon: Palette,
    title: 'Website Design',
    desc: 'Beautiful, custom websites that reflect your brand and appeal to your customers.',
    features: [
      'Custom design tailored to your business',
      'Professional look and feel',
      'User-friendly navigation',
      'Conversion-focused layouts'
    ]
  },
  {
    icon: RefreshCw,
    title: 'Website Rebuilds',
    desc: 'Transform your outdated site into a modern, effective online presence.',
    features: [
      'Complete visual refresh',
      'Improved user experience',
      'Updated content strategy',
      'Better performance'
    ]
  },
  {
    icon: Smartphone,
    title: 'Mobile Optimisation',
    desc: 'Ensure your website looks and works perfectly on all devices.',
    features: [
      'Responsive design',
      'Touch-friendly interfaces',
      'Fast mobile loading',
      'Seamless experience across devices'
    ]
  },
  {
    icon: Search,
    title: 'SEO Basics',
    desc: 'Help customers find you when they search for businesses like yours.',
    features: [
      'Keyword optimisation',
      'Meta tags and descriptions',
      'Search-friendly structure',
      'Local SEO setup'
    ]
  },
  {
    icon: Server,
    title: 'Hosting & Maintenance',
    desc: 'Reliable hosting with ongoing support to keep your site running smoothly.',
    features: [
      'Fast, secure hosting',
      'Regular backups',
      'Security updates',
      'Technical support'
    ]
  },
  {
    icon: Plug,
    title: 'Integrations',
    desc: 'Connect your website to the tools you need to run your business.',
    features: [
      'Booking systems',
      'Contact forms',
      'Analytics tracking',
      'Social media feeds'
    ]
  }
];

const Services = () => {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-6">
              Our Services
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Everything you need to get your business online and keep it running smoothly. 
              No technical jargon, just practical solutions that work.
            </p>
            <Button
              asChild
              data-testid="services-cta-btn"
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg"
            >
              <Link to="/contact">
                Get Started <ChevronRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full border-border/50 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-6 md:p-8">
                    <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center mb-6">
                      <service.icon className="w-7 h-7 text-primary-foreground" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-foreground mb-3">
                      {service.title}
                    </h2>
                    <p className="text-muted-foreground mb-6">{service.desc}</p>
                    <ul className="space-y-3">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <CheckCircle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                          <span className="text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
              Why Work With Us?
            </h2>
            <p className="text-lg text-muted-foreground mb-12">
              We're not a big agency with endless meetings and corporate processes. 
              We're a small studio that cares about getting your website right.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              { title: 'Fast Turnaround', desc: 'Most sites ready in days or weeks, not months.' },
              { title: 'Clear Pricing', desc: 'No hidden fees or surprise costs. You know what you\'re paying.' },
              { title: 'Ongoing Support', desc: 'We stick around after launch to help when you need it.' }
            ].map((item, index) => (
              <div key={index} className="text-center p-6">
                <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                  {item.title}
                </h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
              Tell us about your business and we'll help you figure out exactly what you need.
            </p>
            <Button
              asChild
              data-testid="services-bottom-cta-btn"
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg"
            >
              <Link to="/contact">Get a Free Quote</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
